import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';

// @mui
import {
  Card,
  Grid,
  Stack,
  Popper,
  MenuItem,
  Container,
  Typography,
  Pagination,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';

import Scrollbar from 'src/components/scrollbar/Scrollbar';
import TableToolbar from 'src/components/ToolBars/TableToolbar';
import ActionButton from 'src/components/action-button/action-button';

import { useAuth } from '../../hooks/useAuth';
import Iconify from '../../components/iconify';
import { useToast } from '../../hooks/useToast';
// ----------------------------------------------------------------------
import { getComparator } from '../../utils/filterData';
import FormList from '../../sections/form/formList/FormList';
// eslint-disable-next-line
import { fetchDepartments } from '../../services/department.service';
import { titleHeadingStyle, titleHeadingStyleSmole } from '../style';
import FormDetails from '../../sections/form/formDetails/FormDetails';
import {
  deleteForm,
  fetchForms,
  fetchFormsByDepartments,
} from '../../services/form.service';

const itemsPerPage = 8;
const order = 'asc';
const orderBy = 'name';

export default function FormsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { showToast, hideToast } = useToast();
  const [page, setPage] = useState(0);
  const [openDetailedView, setOpenDetailedView] = useState(false);
  const [selectedForm, setSelectedForm] = useState(false);
  const [formData, setFormData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [isLoading, setIsloading] = useState(false);

  // The 4 separated state variables for independent searching and filtering
  const [searchBy, setSearchBy] = useState('title');
  const [searchValue, setSearchValue] = useState('');
  const [filterBy, setFilterBy] = useState('');
  const [filterValue, setFilterValue] = useState('');

  // Popover states
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [subFilterPopover, setSubFilterPopover] = useState({ department: null });

  // Filter methods
  const handleOpenFilter = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const { departmentIds, permissions } = useAuth();

  const canCreate = permissions?.includes('FORM:CREATE');
  const canReadGlobal = !departmentIds || departmentIds.length === 0;

  const handleCloseFilter = () => {
    handleCloseSubFilter();
    setPopoverAnchorEl(null);
  };
  const handleOpenSubFilter = (event, category) => {
    if (category === 'department') {
      setSubFilterPopover({ department: event.currentTarget });
    }
  };

  const handleCloseSubFilter = () => {
    setSubFilterPopover({ department: null });
  };

  // Fetch forms
  const getForms = () => {
    setIsloading(true);

    const apiCall = canReadGlobal ? fetchForms : fetchFormsByDepartments;
    const apiArgs = canReadGlobal ? [] : [departmentIds];

    genericApiToaster(
      apiCall,
      apiArgs,
      '',
      t('errorMessages.couldntLoadForms'),
      showToast,
      hideToast,
      t,
      (res) => {
        setFormData(res || []);
      },
      undefined,
      () => setIsloading(false)
    );
  };

  // fetch departments
  const fetchDepartmentData = () => {
    genericApiToaster(
      fetchDepartments,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      setDepartmentData
    );
  };

  useEffect(() => {
    const isReady = canReadGlobal || (!canReadGlobal && departmentIds?.length > 0);

    if (isReady) {
      getForms();
      fetchDepartmentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDetailedView, departmentIds]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  const removeForm = (id) => {
    genericApiToaster(
      deleteForm,
      id,
      t('successMessages.successFormRemove'),
      t('errorMessages.couldntRemoveForm'),
      showToast,
      hideToast,
      t,
      () => {
        const newFormList = formData.filter((form) => form.id !== id);
        setFormData(newFormList);
        setPage(0);
      }
    );
  };

  const filteredForms = useMemo(() => {
    let result = formData;

    // First: Apply categorical dropdown filter (Department)
    if (filterBy && filterValue) {
      const targetVal = Number(filterValue);
      result = result.filter((form) => {
        if (filterBy === 'departmentId' || filterBy === 'department') {
          return Number(form.departmentId) === targetVal;
        }
        return true;
      });
    }

    // Second: Apply text query search (ID, Title, Description)
    if (searchValue) {
      const query = searchValue.toLowerCase().trim();
      result = result.filter((form) => {
        if (searchBy === 'id') {
          return String(form.id).includes(query);
        }
        if (searchBy === 'title') {
          return form.title && form.title.toLowerCase().includes(query);
        }
        if (searchBy === 'description') {
          return form.description && form.description.toLowerCase().includes(query);
        }
        return true;
      });
    }

    // Third: Sort the final filtered array using the template's comparator
    const comparator = getComparator(order, orderBy);
    const stabilizedThis = result.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderCompare = comparator(a[0], b[0]);
      if (orderCompare !== 0) return orderCompare;
      return a[1] - b[1];
    });

    return stabilizedThis.map((el) => el[0]);
  }, [searchValue, searchBy, filterBy, filterValue, formData]); // Fixed: Removed 'order' and 'orderBy' as they are outer-scope static constants

  const { totalPages, isNotFound, searchIsNotFound, paginatedForms } = useMemo(() => {
    const totalPages = Math.ceil(filteredForms.length / itemsPerPage);
    const isNotFound = !filteredForms.length;
    const searchIsNotFound = !filteredForms.length && !!searchValue; // Fixed: Checked searchValue instead of non-existent filterName
    const paginatedForms = filteredForms.slice(
      page * itemsPerPage,
      page * itemsPerPage + itemsPerPage
    );
    return { totalPages, isNotFound, searchIsNotFound, paginatedForms };
  }, [filteredForms, page, searchValue]); // Fixed: Checked searchValue instead of non-existent filterName

  return (
    <>
      <Helmet>
        <title> {t('nav.forms')}</title>
      </Helmet>
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={isSmallScreen ? 2 : 5}
        >
          <Typography
            style={isSmallScreen ? titleHeadingStyleSmole : titleHeadingStyle}
            mx={1}
          >
            {t('nav.forms')}
          </Typography>
          <Grid>
            {canCreate && (
              <ActionButton
                id="create-new-form-btn"
                onClick={() => navigate('/dashboard/NewForm')}
                icon={isSmallScreen ? 'octicon:plus-circle-16' : 'eva:plus-fill'}
                text={t('buttons.newForm')}
                title={t('buttons.newForm')}
              />
            )}
          </Grid>
        </Stack>
        <TableToolbar
          numSelected={0}
          filterName={searchValue} // Maps to searchValue
          setFilterName={setSearchValue} // Maps to setSearchValue
          filterBy={searchBy} // Binds text-search field reference (title, id, description)
          setFilterBy={setSearchBy} // Binds searchBy setter
          setPage={setPage} // Connected to reset pages on query change
          filterValue={filterValue} // Passed down to display active filter Chip
          setFilterValue={setFilterValue} // Passed down to allow Chip clearing
          handleCloseFilter={handleCloseFilter}
          handleOpenFilter={handleOpenFilter}
          onFilterName={(e) => {
            setPage(0);
            setSearchValue(e.target.value);
          }}
          model="Form"
        />
        <FormDetails
          key={selectedForm.id}
          setOpen={setOpenDetailedView}
          open={openDetailedView}
          formId={selectedForm.id}
          initialData={selectedForm.formData}
          setSelectedForm={setSelectedForm}
        />
        <FormList
          forms={paginatedForms}
          removeForm={removeForm}
          setOpenDetailedView={setOpenDetailedView}
          setSelectedForm={setSelectedForm}
          isLoading={isLoading}
          isNotFound={isNotFound}
          searchIsNotFound={searchIsNotFound}
          filterName={searchValue} // Fixed: Passes searchValue state
          departmentsData={departmentData}
        />
        <Popper
          open={Boolean(popoverAnchorEl)}
          anchorEl={popoverAnchorEl}
          placement={isSmallScreen ? 'bottom' : 'left-start'}
          onClose={handleCloseFilter}
        >
          <Card
            sx={{
              p: 1,
              minwidth: isSmallScreen ? 110 : 140,
              '& .MuiMenuItem-root': {
                px: 1,
                typography: 'body2',
                borderRadius: 0.75,
                boxShadow: '0.5px 0.5px 2 px',
              },
            }}
          >
            {/* Category Filter: Department (Always visible to ensure the popover card renders) */}
            <MenuItem
              selected={filterBy === 'department'}
              onMouseEnter={(e) => handleOpenSubFilter(e, 'department')}
              onClick={(e) => handleOpenSubFilter(e, 'department')}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify
                  icon="eva:arrow-ios-back-outline"
                  sx={{
                    mr: 1,
                    width: isSmallScreen ? 16 : 20,
                    height: isSmallScreen ? 16 : 20,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: isSmallScreen && '0.7rem' }}>
                  {t('filters.byDepartment')}
                </Typography>
              </Stack>
            </MenuItem>
          </Card>
        </Popper>
        <Popper
          open={Boolean(subFilterPopover.department)}
          anchorEl={subFilterPopover.department}
          placement="left-start"
          onClose={handleCloseSubFilter}
        >
          <Card
            sx={{
              p: 1,
              minwidth: 140,
              '& .MuiMenuItem-root': {
                px: 1,
                typography: 'body2',
                borderRadius: 0.75,
                boxShadow: '0.5px 0.5px 2 px',
              },
            }}
          >
            <Scrollbar sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {departmentData
                // Securely filters options: Global admins see all, scoped users see only their active memberships
                .filter((dep) => canReadGlobal || (departmentIds || []).includes(dep.id))
                .map((department) => (
                  <MenuItem
                    key={department.id}
                    onClick={() => {
                      setFilterBy('departmentId');
                      setSearchBy('byDepartment');
                      setFilterValue(department.id);
                      handleCloseFilter();
                      handleCloseSubFilter();
                      setPage(0);
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: isSmallScreen && '0.7rem' }}
                    >
                      {department.name}
                    </Typography>
                  </MenuItem>
                ))}
            </Scrollbar>
          </Card>
        </Popper>
        {filteredForms.length > 0 && (
          <Stack alignItems="center" sx={{ mt: 5 }}>
            <Pagination
              component="div"
              color="primary"
              count={totalPages}
              size={isSmallScreen ? 'medium' : 'large'}
              page={page + 1}
              onChange={handleChangePage}
              sx={{
                '& .MuiPaginationItem-root': { fontSize: isSmallScreen && '0.75rem' },
              }}
            />
          </Stack>
        )}
      </Container>
    </>
  );
}
